import type { ReactNode } from 'react';
import { NavLink } from 'react-router';
import { cn } from '@/shared/lib/cn';
import type { NavigationItem } from './navigation';

const LINK_CLASS = 'flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap';

function MenuLink({ item }: { item: NavigationItem & { to: string } }): ReactNode {
  return (
    <NavLink
      to={item.to}
      end
      className={({ isActive }) =>
        cn(
          LINK_CLASS,
          isActive
            ? 'bg-accent font-medium text-accent-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )
      }
    >
      <item.icon aria-hidden className="size-4" />
      {item.label}
    </NavLink>
  );
}

/**
 * Lista do menu principal, em árvore: links levam à tela; grupos sem link (ex.: "Financeiro")
 * aparecem como título; subitens ficam recuados.
 */
export function NavigationMenu({
  items,
  nested = false,
}: {
  items: readonly NavigationItem[];
  nested?: boolean;
}): ReactNode {
  return (
    <ul className={cn('flex gap-1 md:flex-col', nested && 'md:ml-4 md:border-l md:pl-2')}>
      {items.map((item) => (
        <li key={item.label} className="md:space-y-1">
          {item.to === undefined ? (
            <span
              className={cn(
                LINK_CLASS,
                'font-semibold tracking-wide text-foreground uppercase md:mt-3 md:text-xs',
              )}
            >
              <item.icon aria-hidden className="size-4" />
              {item.label}
            </span>
          ) : (
            <MenuLink item={{ ...item, to: item.to }} />
          )}
          {item.children !== undefined && <NavigationMenu items={item.children} nested />}
        </li>
      ))}
    </ul>
  );
}
